import React from 'react';
import { Link } from 'react-router-dom';
import {
  Mail,
  MapPin,
  Phone,
  Clock,
  DollarSign,
  TrendingUp,
  Linkedin,
  Facebook
} from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerSections = [
    {
      title: 'Startups',
      links: [
        { name: 'Fundraising Assistance', path: '/services/fundraising-assistance' },
        { name: 'Investor Discovery and Mapping', path: '/services/investor-discovery' },
        { name: 'Investor Outreach', path: '/services/investor-outreach' },
        { name: 'Data Room Creation', path: '/services/data-room' },
        { name: 'Pitch Deck', path: '/services/pitch-deck' },
        { name: 'Financial Model Creation', path: '/services/financial-modeling' }
      ]
    },
    {
      title: 'Investor',
      links: [
        { name: 'Startup Scouting', path: '/for-investors/startup-scouting' },
        { name: 'Startup Matchmaking', path: '/for-investors/startup-matchmaking' },
        { name: 'Fundraising Assistance', path: '/for-investors/fundraising-assistance' },
        { name: 'Strategic Acquisition', path: '/for-investors/strategic-acquisition' }
      ]
    },
    {
      title: 'Resources',
      links: [
        { name: 'Blog', path: '/resources' },
        { name: 'Contact Us', path: '/contact' },
        { name: 'Case Studies', path: '/case-studies' },
        { name: 'Refund Policy', path: '/refund-policy' }
      ]
    },
    {
      title: 'Company',
      links: [
        { name: 'About Us', path: '/about' },
        { name: 'Career', path: '/career' },
        { name: 'Reviews', path: '/reviews' },
        { name: 'Terms & Conditions', path: '/terms' },
        { name: 'Privacy Policy', path: '/privacy' }
      ]
    }
  ];

  const stageLinks = [
    { name: 'Early Stage', path: '/stage/early' },
    { name: 'Growth Stage', path: '/stage/growth' },
    { name: 'Late Stage', path: '/stage/late' }
  ];

  const investorTypeLinks = [
    { name: 'HNI & Angel', path: '/investor-type/hni-angel' },
    { name: 'Micro VCs/VCs', path: '/investor-type/micro-vcs' },
    { name: 'Family Offices', path: '/investor-type/family-offices' },
    { name: 'Private Equity', path: '/investor-type/private-equity' },
    { name: 'Corporate Investors', path: '/investor-type/corporate' }
  ];

  return (
    <footer className="bg-gradient-to-br from-gray-950 via-emerald-950 to-gray-950 text-white">
      {/* Main Footer */}
      <div className="container mx-auto px-4 py-16">

        {/* Brand Section */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12 pb-12 border-b border-green-800/30">

          <div className="lg:col-span-4">
            <Link to="/" className="flex items-center gap-3 mb-6 group">

              <div className="relative w-14 h-14">
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-400 via-green-500 to-emerald-600 rounded-2xl shadow-lg transform rotate-6"></div>

                <div className="absolute inset-0 bg-gradient-to-br from-emerald-600 via-green-700 to-emerald-800 rounded-2xl shadow-xl flex items-center justify-center -rotate-3">
                  <svg viewBox="0 0 24 24" className="w-8 h-8" fill="none">
                    <rect x="3" y="14" width="3" height="7" rx="0.5" fill="white" opacity="0.5"/>
                    <rect x="7" y="10" width="3" height="11" rx="0.5" fill="white" opacity="0.7"/>
                    <rect x="11" y="6" width="3" height="15" rx="0.5" fill="white" opacity="0.9"/>
                    <rect x="15" y="3" width="3" height="18" rx="0.5" fill="white"/>
                  </svg>
                </div>
              </div>

              <div>
                <div className="text-2xl font-bold text-white leading-tight">
                  FounderScale
                </div>

                <div className="text-xs text-emerald-400 font-bold leading-tight tracking-wider">
                  CAPITAL
                </div>

                <div className="text-[10px] text-gray-400 italic mt-1">
                  From Pitch to Capital
                </div>
              </div>

            </Link>

            <p className="text-green-200 mb-6 leading-relaxed text-sm">
              Making founders fundable through honest guidance, strategic execution,
              and direct investor access. Trusted by founders from ideation to pre-Series A.
            </p>

            <div className="space-y-3">

              <div className="flex items-start gap-3">
                <Mail className="h-4 w-4 text-emerald-400 mt-1 flex-shrink-0" />

                <a
                  href="mailto:info@founderscale.capital"
                  className="text-green-200 hover:text-emerald-300 transition-colors text-sm"
                >
                  info@founderscale.capital
                </a>
              </div>

              <div className="flex items-start gap-3">
                <Phone className="h-4 w-4 text-emerald-400 mt-1 flex-shrink-0" />

                <a
                  href="tel:+919953022193"
                  className="text-green-200 hover:text-emerald-300 transition-colors text-sm"
                >
                  +91 99530 22193
                </a>
              </div>

              <div className="flex items-center gap-2 text-emerald-400 pt-2">
                <Clock className="h-4 w-4" />

                <span className="text-xs font-semibold uppercase tracking-wider">
                  Available 24/7
                </span>
              </div>

            </div>

            {/* Social Media Links */}
            <div className="flex gap-3 mt-6">

              {/* LinkedIn */}
              <a
                href="https://www.linkedin.com/company/founderscale-capital/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="FounderScale Capital on LinkedIn"
                className="w-10 h-10 bg-white/5 hover:bg-emerald-500 rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-110"
              >
                <Linkedin className="h-4 w-4 text-white" />
              </a>

              {/* Facebook */}
              <a
                href="https://www.facebook.com/profile.php?id=61594830844840"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="FounderScale Capital on Facebook"
                className="w-10 h-10 bg-white/5 hover:bg-emerald-500 rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-110"
              >
                <Facebook className="h-4 w-4 text-white" />
              </a>

            </div>

          </div>

          {/* Footer Links Grid */}
          <div className="lg:col-span-8 grid grid-cols-2 md:grid-cols-4 gap-8">

            {footerSections.map((section, index) => (
              <div key={index}>

                <h3 className="text-xs font-bold text-white uppercase tracking-widest mb-5">
                  {section.title}
                </h3>

                <ul className="space-y-3">

                  {section.links.map((link) => (
                    <li key={link.path}>

                      <Link
                        to={link.path}
                        className="text-green-200/80 hover:text-emerald-300 transition-colors duration-200 text-sm leading-relaxed"
                      >
                        {link.name}
                      </Link>

                    </li>
                  ))}

                </ul>

              </div>
            ))}

          </div>

        </div>

        {/* Second Row: Stage & Investor Type */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12 pb-12 border-b border-green-800/30">

          <div>

            <h3 className="text-xs font-bold text-white uppercase tracking-widest mb-5">
              By Stage Funding
            </h3>

            <ul className="space-y-3">

              {stageLinks.map((link) => (
                <li key={link.path}>

                  <Link
                    to={link.path}
                    className="text-green-200/80 hover:text-emerald-300 transition-colors duration-200 text-sm"
                  >
                    {link.name}
                  </Link>

                </li>
              ))}

            </ul>

          </div>

          <div>

            <h3 className="text-xs font-bold text-white uppercase tracking-widest mb-5">
              Investor Type
            </h3>

            <ul className="space-y-3">

              {investorTypeLinks.map((link) => (
                <li key={link.path}>

                  <Link
                    to={link.path}
                    className="text-green-200/80 hover:text-emerald-300 transition-colors duration-200 text-sm"
                  >
                    {link.name}
                  </Link>

                </li>
              ))}

            </ul>

          </div>

        </div>

        {/* Office Addresses */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">

          <div>
            <div className="flex items-center gap-2 mb-2">
              <MapPin className="h-4 w-4 text-emerald-400" />
              <span className="text-white font-bold text-sm">
                India
              </span>
            </div>

            <p className="text-green-200/70 text-xs leading-relaxed">
              WeWork Galaxy, 43, Residency Road, Bengaluru, Karnataka 560025
            </p>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-2">
              <MapPin className="h-4 w-4 text-emerald-400" />
              <span className="text-white font-bold text-sm">
                USA
              </span>
            </div>

            <p className="text-green-200/70 text-xs leading-relaxed">
              1209 Orange Street, Wilmington, Delaware 19801
            </p>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-2">
              <MapPin className="h-4 w-4 text-emerald-400" />
              <span className="text-white font-bold text-sm">
                Canada
              </span>
            </div>

            <p className="text-green-200/70 text-xs leading-relaxed">
              100 King Street West, Suite 5700, Toronto, ON M5X 1C7
            </p>
          </div>

        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-green-800/30">

          <div className="flex flex-col md:flex-row justify-between items-center gap-4">

            <p className="text-green-300/60 text-sm">
              © {currentYear} FounderScale Capital. All rights reserved.
            </p>

            <p className="text-green-300/60 text-sm italic">
              Built for founders, by founders.
            </p>

          </div>

        </div>

      </div>
    </footer>
  );
};

export default Footer;
